import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { isHttpClientError, resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import {
  APPROVAL_NOT_APPROVER_CODE,
  APPROVAL_ROUND_CLOSED_CODE,
  APPROVAL_ROUNDS_PAGE_SIZE,
  APPROVAL_STEP_HANDLED_CODE,
  approvalRoundDetailQueryKey,
  approvalRoundsQueryKey,
} from "../config";
import {
  approveApprovalRound,
  fetchApprovalRoundDetail,
  fetchApprovalRounds,
  rejectApprovalRound,
} from "../repo/approvalRoundsRepo";
import { validateRejectRemark } from "../service/approvalRoundsService";
import type { ApprovalInboxTab, ApprovalRoundDetail } from "../types";

/** 待我审批页：两个列表、详情抽屉、通过与驳回；处理时带页面上看到的当前级，防止重复提交。 */
export function useApprovalInboxRuntime() {
  const client = useQueryClient();
  const [tab, setTab] = useState<ApprovalInboxTab>("pending");
  const [page, setPage] = useState(1);
  const [roundId, setRoundId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"approve" | "reject" | null>(null);
  const [remark, setRemark] = useState("");
  const [remarkError, setRemarkError] = useState("");
  const [actionError, setActionError] = useState("");
  const [acting, setActing] = useState(false);
  const actLock = useRef(false);
  const listQuery = useQuery({
    queryKey: [...approvalRoundsQueryKey, tab, page],
    queryFn: () => fetchApprovalRounds(tab, page, APPROVAL_ROUNDS_PAGE_SIZE),
    refetchOnWindowFocus: false,
  });
  const detailQuery = useQuery({
    queryKey: [...approvalRoundDetailQueryKey, roundId],
    queryFn: () => fetchApprovalRoundDetail(roundId as string),
    enabled: roundId !== null,
    refetchOnWindowFocus: false,
    // 读不到多半是别人已处理、单子已不归我，重试只会让抽屉空等、弹几次提示；网络抖动由 httpClient 自己重试。
    retry: false,
  });
  // 处理完最后一页上唯一的一张，这一页就没了：退回最后一页。
  useEffect(() => {
    const total = listQuery.data?.total;
    if (total === undefined) return;
    const maxPage = Math.max(1, Math.ceil(total / APPROVAL_ROUNDS_PAGE_SIZE));
    if (page > maxPage) setPage(maxPage);
  }, [listQuery.data?.total, page]);
  // 重读失败时不再沿用旧数据：别人处理过之后这张单子可能已不归我，旧数据里还带着能处理的按钮。
  const detail = roundId && !detailQuery.error ? detailQuery.data ?? null : null;
  const resetDialog = () => { setDialog(null); setRemark(""); setRemarkError(""); };

  return {
    tab,
    setTab(next: ApprovalInboxTab) { setTab(next); setPage(1); },
    page,
    setPage,
    items: listQuery.data?.items ?? [],
    total: listQuery.data?.total ?? 0,
    loading: listQuery.isFetching,
    listError: listQuery.error ? "列表加载失败，请重试。" : "",
    refreshList() { void listQuery.refetch(); },
    roundId,
    detail,
    detailLoading: detailQuery.isFetching,
    detailError: describeDetailError(detailQuery.error),
    openRound(id: string) { setRoundId(id); setActionError(""); resetDialog(); },
    closeRound() { if (!actLock.current) { setRoundId(null); setActionError(""); resetDialog(); } },
    dialog,
    openDialog(action: "approve" | "reject") { setDialog(action); setRemark(""); setRemarkError(""); setActionError(""); },
    closeDialog() { if (!actLock.current) resetDialog(); },
    remark,
    setRemark(value: string) { setRemark(value); setRemarkError(""); },
    remarkError,
    actionError,
    acting,
    /** 提交通过或驳回；驳回先在前端校验意见。别人已处理或这一轮已结束时提示并刷新列表和详情。 */
    async act() {
      if (!detail || !dialog || actLock.current) return false;
      const text = remark.trim();
      if (dialog === "reject") {
        const error = validateRejectRemark(text);
        if (error) { setRemarkError(error); return false; }
      }
      actLock.current = true; setActing(true); setActionError("");
      try {
        const updated: ApprovalRoundDetail = dialog === "approve"
          ? await approveApprovalRound(detail.roundId, detail.currentStepNo, text || null)
          : await rejectApprovalRound(detail.roundId, detail.currentStepNo, text);
        client.setQueryData([...approvalRoundDetailQueryKey, updated.roundId], updated);
        resetDialog();
        void client.invalidateQueries({ queryKey: approvalRoundsQueryKey });
        return true;
      } catch (error) {
        setActionError(resolveReadableHttpErrorMessage(error, "处理失败，请稍后重试。"));
        if (isHttpClientError(error) && (error.code === APPROVAL_STEP_HANDLED_CODE || error.code === APPROVAL_ROUND_CLOSED_CODE)) {
          // 先收起按钮再重读：我若也是下一级的审批人，重读后按钮会按下一级重新出现。
          client.setQueryData<ApprovalRoundDetail>([...approvalRoundDetailQueryKey, detail.roundId], (old) => old && { ...old, canHandle: false });
          resetDialog();
          void client.invalidateQueries({ queryKey: approvalRoundsQueryKey });
          void client.invalidateQueries({ queryKey: approvalRoundDetailQueryKey });
        }
        return false;
      } finally {
        actLock.current = false; setActing(false);
      }
    },
  };
}

/** 详情读不到的提示；不是审批人（多半是别人已处理、单子已不归我）单独说明。 */
function describeDetailError(error: unknown): string {
  if (!error) return "";
  if (isHttpClientError(error) && error.code === APPROVAL_NOT_APPROVER_CODE) return "这张单子已经不归你处理了。";
  return resolveReadableHttpErrorMessage(error, "详情加载失败，请重试。");
}
